/**
 * The React admin's only door to the backend: the server's
 * /api/v1/admin/* API, on this site's own address (the web server in
 * front of the admin passes /api/v1 through to the API server). The
 * browser's session cookie — set by the server at sign-in, unreadable by
 * scripts — authenticates every call. There is no secret here.
 *
 * Never throws: returns a discriminated result.
 */

export type ServerResult<T> =
  | { ok: true; data: T }
  | { ok: false; status: number; error: string; code?: string; details?: unknown };

export type ServerMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

interface ServerErrorBody {
  message?: string;
  code?: string;
  details?: unknown;
}

// A hung server must not hang a screen. An export builds a file.
const CALL_TIMEOUT_MS = 15_000;
const FILE_TIMEOUT_MS = 60_000;

let unauthorizedHandler: (() => void) | null = null;

/** Called when the server says the session is gone (expired, or the admin was removed). */
export function onUnauthorized(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/** A validation failure's useful text is on its first field. */
function errorText(err: ServerErrorBody, status: number): string {
  const first = Array.isArray(err.details) ? (err.details[0] as { message?: string } | undefined) : undefined;
  return first?.message ?? err.message ?? `Server responded ${status}`;
}

function fetchFailure(err: unknown): { status: number; error: string } {
  if (err instanceof Error && err.name === 'TimeoutError') {
    return { status: 504, error: 'The server did not respond in time' };
  }
  return { status: 502, error: err instanceof Error ? err.message : 'Server unreachable' };
}

interface CallOptions {
  body?: unknown;
  /**
   * False when the call is itself the question "is anyone signed in?".
   * A "no" is then an ordinary answer, not a lost session — reporting it
   * would send the app to the login page, which asks again, forever.
   */
  expectSignedIn?: boolean;
}

export async function callServer<T>(
  method: ServerMethod,
  path: string,
  { body, expectSignedIn = true }: CallOptions = {},
): Promise<ServerResult<T>> {
  try {
    const res = await fetch(path, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'content-type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: AbortSignal.timeout(CALL_TIMEOUT_MS),
    });
    const payload = (await res.json().catch(() => ({}))) as { data?: T; error?: ServerErrorBody | null };
    if (!res.ok) {
      const err = payload.error ?? {};
      if (expectSignedIn && res.status === 401 && err.code === 'ADMIN_UNAUTHORIZED') {
        unauthorizedHandler?.();
      }
      return {
        ok: false,
        status: res.status,
        error: errorText(err, res.status),
        ...(err.code && { code: err.code }),
        ...(err.details !== undefined && err.details !== null && { details: err.details }),
      };
    }
    return { ok: true, data: payload.data as T };
  } catch (err) {
    return { ok: false, ...fetchFailure(err) };
  }
}

/** GET a file (e.g. an .xlsx export) and hand it back as-is; a refusal is `{ error }` JSON. */
export async function fetchServerFile(path: string): Promise<Response> {
  try {
    const res = await fetch(path, { credentials: 'same-origin', signal: AbortSignal.timeout(FILE_TIMEOUT_MS) });
    if (res.ok) return res;
    const payload = (await res.json().catch(() => ({}))) as { error?: ServerErrorBody | null };
    return Response.json({ error: errorText(payload.error ?? {}, res.status) }, { status: res.status });
  } catch (err) {
    const { status, error } = fetchFailure(err);
    return Response.json({ error }, { status });
  }
}
